import {Alert, Button, Col, Modal, Row, Space, Spin, Table} from "antd";
import {
  useCustomerPastEvents,
  type CustomerLiveEventBooking,
  type LegacyCustomerEvent,
} from "../../../../features/customer-live-events";
import {FullPageLoader} from "../../../full-page-loader";
import {
  StyledDescriptionText,
  StyledNoUpcomingCallBox,
} from "../../../calls/upcoming-calls/upcoming-calls.style";
import {ColumnsType} from "antd/lib/table";
import {useState} from "react";
import {
  StyledDescriptionTextValue,
  StyledInfoItem,
} from "../../../customers/details-page/info-section/info-section.style";
import {StyledModalContentWrapper} from "../../../calls/upcoming-calls/call-card/call-card.style";
import {LoadingSpinner} from "../../../icons/icons";
import {CustomPagination} from "../../../pagination";

type PastEventType = LegacyCustomerEvent | CustomerLiveEventBooking;

function isLegacyEvent(event: PastEventType): event is LegacyCustomerEvent {
  return "event_date" in event;
}

function formatPrice(event: PastEventType): string {
  if (isLegacyEvent(event)) {
    return event.liveEvent.callPrice === 0
      ? "Free"
      : `${event.liveEvent.callPriceCurrency} ${event.liveEvent.callPrice.toLocaleString(
          "en-US"
        )}`;
  }
  const price = event.liveEvent.callPrice ?? event.amount;
  const currency = event.liveEvent.callPriceCurrency ?? event.currency;
  return price === 0 ? "Free" : `${currency} ${price.toLocaleString("en-US")}`;
}

function getDescription(event: PastEventType): string {
  if (isLegacyEvent(event)) {
    return event.liveEvent.description;
  }
  return event.liveEvent.description ?? "";
}

function getAttendees(event: PastEventType): number | undefined {
  if (isLegacyEvent(event)) {
    return event.liveEvent.attendees;
  }
  return event.liveEvent.attendees;
}

interface DataType {
  key: string;
  name: string;
  price: string;
  description: string;
}

const CustomerPastLiveClasses = () => {
  const [page, setPage] = useState(1);
  const perPage = 5;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState<PastEventType>();

  const handleOk = () => {
    setIsModalOpen(false);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  const getLiveClassesQuery = useCustomerPastEvents({page, perPage});

  const pastEvents = getLiveClassesQuery.data?.data ?? [];
  const paginationMeta = getLiveClassesQuery.data
    ? {
        current_page: page,
        per_page: perPage,
        total_pages: Math.ceil(getLiveClassesQuery.data.total / perPage),
        next_page: getLiveClassesQuery.data.hasNextPage ? page + 1 : 0,
        previous_page: getLiveClassesQuery.data.hasPrevPage ? page - 1 : 0,
      }
    : undefined;

  if (getLiveClassesQuery.isLoading) {
    return <FullPageLoader />;
  }

  if (
    getLiveClassesQuery.isError &&
    getLiveClassesQuery.error.message !==
      "Sorry, You currently do not have any live events!"
  ) {
    return (
      <Alert
        message="Error"
        description={getLiveClassesQuery.error.message}
        type="error"
        showIcon
      />
    );
  }

  const pastClassData: DataType[] = pastEvents.map((pastClass) => ({
    key: pastClass.id.toString(),
    name: pastClass.liveEvent.name,
    description: getDescription(pastClass),
    price: formatPrice(pastClass),
  }));

  const showModal = (id: string) => {
    const content = pastEvents.find((data) => data.id === +id);
    if (content) {
      setModalContent(content);
      setIsModalOpen(true);
    }
  };

  const columns: ColumnsType<DataType> = [
    {
      title: "Name",
      dataIndex: "name",
      fixed: "left",
      width: "17.425rem",
      key: "name",
      render: (text) => (
        <>
          <StyledDescriptionText style={{textTransform: "capitalize"}}>
            {text}
          </StyledDescriptionText>
        </>
      ),
    },
    {
      title: "Price",
      dataIndex: "price",
      width: "17.425rem",
      key: "price",
      render: (text) => (
        <>
          <StyledDescriptionText>{text}</StyledDescriptionText>
        </>
      ),
    },
    {
      title: "Description",
      dataIndex: "description",
      width: "17.425rem",
      key: "description",
      render: (text) =>
        text ? (
          <StyledDescriptionTextValue>
            {text.replace(/<[^>]+>/g, "")}
          </StyledDescriptionTextValue>
        ) : (
          <StyledDescriptionTextValue style={{color: "#999"}}>
            —
          </StyledDescriptionTextValue>
        ),
    },
    {
      title: "",
      key: "action",
      render: (_, record) => (
        <Button size="small" type="link" onClick={() => showModal(record.key)}>
          <span style={{textDecoration: "underline"}}>View details</span>
        </Button>
      ),
    },
  ];
  return (
    <Space direction="vertical" size={29} style={{display: "flex"}}>
      {pastEvents.length > 0 ? (
        <>
          <Table
            columns={columns}
            dataSource={pastClassData}
            loading={getLiveClassesQuery.isFetching}
            scroll={{x: 100}}
            pagination={false}
          />
          <CustomPagination
            isPreviousData={getLiveClassesQuery.isPreviousData}
            page={page}
            paginationMeta={paginationMeta}
            setPage={setPage}
          />
        </>
      ) : (
        <Spin
          spinning={getLiveClassesQuery.isFetching}
          tip="Updating..."
          indicator={LoadingSpinner}
        >
          <div style={{width: "100%"}}>
            <StyledNoUpcomingCallBox>
              <p className="info-text">
                You do not have any past live event at the moment.
              </p>
            </StyledNoUpcomingCallBox>
          </div>
        </Spin>
      )}
      <Modal
        title={modalContent?.liveEvent.name}
        visible={isModalOpen}
        onOk={handleOk}
        onCancel={handleCancel}
        okButtonProps={{
          size: "small",
        }}
        cancelButtonProps={{
          size: "small",
        }}
      >
        <StyledModalContentWrapper>
          <Row gutter={[32, 20]}>
            <Col span={24} md={8}>
              <StyledInfoItem>
                <p className="info-title">Name</p>
                <p className="info-value">{modalContent?.liveEvent.name}</p>
              </StyledInfoItem>
            </Col>
            <Col span={24} md={8}>
              <StyledInfoItem>
                <p className="info-title">Price</p>
                <p className="info-value">
                  {modalContent ? formatPrice(modalContent) : "—"}
                </p>
              </StyledInfoItem>
            </Col>
            {modalContent && getAttendees(modalContent) !== undefined && (
              <Col span={24} md={8}>
                <StyledInfoItem>
                  <p className="info-title">Attendees</p>
                  <p className="info-value">
                    {getAttendees(modalContent) === -1
                      ? "Unlimited"
                      : getAttendees(modalContent)}
                  </p>
                </StyledInfoItem>
              </Col>
            )}
            <Col span={24}>
              <StyledInfoItem>
                <p className="info-title">Description</p>
                <div
                  className="info-value"
                  dangerouslySetInnerHTML={{
                    __html:
                      (modalContent && getDescription(modalContent)) || "No description",
                  }}
                />
              </StyledInfoItem>
            </Col>
          </Row>
        </StyledModalContentWrapper>
      </Modal>
    </Space>
  );
};

export {CustomerPastLiveClasses};
